import { ShiftOptions } from '@floating-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, useState, useMemo } from 'react';
import { useMeasure } from 'react-use';
import { useCreateMessage, useMessages } from '../../../../Message';
import { useScrollToLatestVirtualThreadMessage, useInstaScrollToVirtualThreadEnd } from '../../../hooks';
import { setupVirtualMessagingThreadItems } from '../../../util';
import { useSelf } from '../../../../User';
import { FILE_LIST_DEFAULT_FLOATING_SHIFT } from '../../MessageExpandableTagList';
import { useAdvisorCreateMessageForm } from '../../MessageForm';
import { useFirmClient } from '../../../../FirmClient';
import { useConversation } from '../../../../Conversation/queries';
import { conversationKeys } from '../../../../Conversation';
import { findFirmMemberForSelf } from '../../../../FirmMember/util';
import { useFirmMembersForFirm } from '../../../../FirmMember';
import { useTranslation } from 'react-i18next';

export interface UseAdvisorFloatingThreadWindowArgs {
  firmID: string;
  firmClientID: string;
  firmMemberForSelfID: string;
}

export function useAdvisorFloatingThreadWindow({
  firmID,
  firmClientID,
  firmMemberForSelfID,
}: UseAdvisorFloatingThreadWindowArgs) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t: tMessaging } = useTranslation('inAppMessaging');
  const [expandedTagListID, setExpandedTagListID] = useState<string>();
  const [footerRef, { height: footerHeight }] = useMeasure<HTMLDivElement>();

  const { self } = useSelf();
  const { createMessage } = useCreateMessage();
  const { firmMembers } = useFirmMembersForFirm(firmID);
  const { firmClient } = useFirmClient({ firmClientID, firmID });
  const { conversation, conversationQuery } = useConversation({ firmClientID });
  const { messages, messagesQuery } = useMessages(conversation?.id, { refetchInterval: 10000 });

  // Variables
  const isNewConversation = !conversationQuery.isLoading && !conversation?.latestMessageID;
  const isLoading = conversationQuery.isLoading || messagesQuery.isLoading;
  const fileListShiftOverride: ShiftOptions = {
    crossAxis: true,
    padding: { ...FILE_LIST_DEFAULT_FLOATING_SHIFT, bottom: footerHeight + 64 },
  };
  const firmMemberForSelf = findFirmMemberForSelf(firmMembers, self);

  // Form setup
  const useFormReturn = useAdvisorCreateMessageForm({
    defaultValues: { message: '', pendingFiles: [], hasOnboardingReminder: false },
    handleSubmit: ({ formValues: { message, pendingFiles, hasOnboardingReminder } }) => {
      if (!conversation) throw new Error('Conversation not found');

      createMessage.mutate({
        pendingFiles,
        senderID: firmMemberForSelfID,
        conversationQueryKey: conversationKeys.conversation({ firmClientID }),
        createMessageInput: {
          firmClientID,
          conversationID: conversation.id,
          message: {
            hasOnboardingReminder,
            freeform: {
              content: message,
              attachmentFileIDs: pendingFiles.map((f) => f.id),
            },
          },
        },
      });
      useFormReturn.formMethods.setValue('message', '');
      useFormReturn.formMethods.setValue('pendingFiles', []);
      useFormReturn.formMethods.setValue('hasOnboardingReminder', false);
    },
  });

  // Virtualizer setup
  const virtualizerItems = useMemo(() => setupVirtualMessagingThreadItems(messages), [messages]);
  const threadVirtualizer = useVirtualizer({
    gap: 16,
    overscan: 10,
    paddingStart: 24,
    // Overshooting so that the scroll to item works
    scrollPaddingEnd: 300,
    estimateSize: () => 40,
    count: virtualizerItems.length,
    getScrollElement: () => scrollContainerRef.current,
  });

  // Scrolling behavior
  useScrollToLatestVirtualThreadMessage({ virtualizer: threadVirtualizer, messagesLength: messages.length });
  useInstaScrollToVirtualThreadEnd({ virtualizer: threadVirtualizer, effectDeps: [messagesQuery.isSuccess] });

  // Functions
  function handleSendCalendarSchedulingLink() {
    if (!firmMemberForSelf?.calendarSchedulingLink) return;
    useFormReturn.formMethods.setValue(
      'message',
      (useFormReturn.formMethods.getValues('message').trimEnd() + ' ' + firmMemberForSelf.calendarSchedulingLink).trim()
    );
    useFormReturn.formMethods.setFocus('message', { shouldSelect: true });
  }

  return {
    footerRef,
    scrollContainerRef,
    expandedTagListID,
    threadVirtualizer,
    virtualizerItems,
    self,
    setExpandedTagListID,
    conversation,
    fileListShiftOverride,
    useFormReturn,
    createMessage,
    isNewConversation,
    isLoading,
    firmClient,
    firmMemberForSelf,
    tMessaging,
    handleSendCalendarSchedulingLink,
  };
}
